import { useState, type CSSProperties } from "react";
import {
  startBillingCheckout,
  cancelBillingSubscription,
  downloadMyData,
  deleteMyAccount,
} from "../lib/api";

const NAVY = "#0b1f4a";
const ACCENT_700 = "#7d5411";
const LINK = "#1a3a7a";
const DANGER = "#B42318";

const LANGUAGES = [
  { code: "sv", label: "Svenska" },
  { code: "nb", label: "Norsk (bokmål)" },
  { code: "nn", label: "Norsk (nynorsk)" },
  { code: "da", label: "Dansk" },
  { code: "fi", label: "Suomi" },
  { code: "is", label: "Íslenska" },
  { code: "en", label: "English" },
  { code: "de", label: "Deutsch" },
  { code: "fr", label: "Français" },
  { code: "es", label: "Español" },
  { code: "pl", label: "Polski" },
  { code: "nl", label: "Nederlands" },
];

type ModalKind = "cancel-step1" | "cancel-step2" | "delete" | null;

/**
 * Konto — plan 29 kr, cancel 2-step (no dark pattern), language list, export, delete confirm.
 * Mock only. Route suggestion: /account or /konto
 */
export default function Account() {
  const [lang, setLang] = useState("sv");
  const [modal, setModal] = useState<ModalKind>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [planActive, setPlanActive] = useState(() => {
    try {
      return !!localStorage.getItem("gdpr_nordic_paypal_profile") ||
        !!localStorage.getItem("gdpr_nordic_plan");
    } catch {
      return false;
    }
  });
  const [billingBusy, setBillingBusy] = useState(false);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2800);
  };

  const payWithPaypal = async (planId: string) => {
    setBillingBusy(true);
    try {
      const res = await startBillingCheckout(planId);
      window.location.href = res.data.approveUrl;
    } catch (e) {
      showToast(
        e instanceof Error ? e.message : "PayPal kunde inte startas"
      );
      setBillingBusy(false);
    }
  };

  const card: CSSProperties = {
    background: "#fff",
    border: "1px solid color-mix(in srgb, #201f1d 16%, transparent)",
    borderRadius: 7,
    padding: "22px 26px",
  };

  const ghostBtn = (danger = false): CSSProperties => ({
    padding: "9px 16px",
    border: danger ? `1px solid ${DANGER}` : "1px solid color-mix(in srgb, #201f1d 16%, transparent)",
    borderRadius: 4,
    background: "transparent",
    fontSize: 13,
    color: danger ? DANGER : "#605d5d",
    cursor: "pointer",
    fontFamily: "inherit",
  });

  /**
   * Sa tidigare "Prenumerationen avslutad via PayPal" i tre lägen där inget
   * avslutats: när profil-id saknades i localStorage hoppades anropet över
   * helt, och catch-grenen satte också planActive = false. Kunden trodde att
   * abonnemanget var uppsagt och fortsatte debiteras. Nu ändras läget bara
   * när PayPal faktiskt bekräftat.
   */
  const confirmCancel = async () => {
    setModal(null);
    const profileId = localStorage.getItem("gdpr_nordic_paypal_profile");
    if (!profileId) {
      showToast(
        "Vi hittar ingen prenumeration att avsluta här. Köpte du i appen avslutar du den i App Store eller Google Play."
      );
      return;
    }
    try {
      await cancelBillingSubscription(profileId);
      localStorage.removeItem("gdpr_nordic_paypal_profile");
      setPlanActive(false);
      showToast("Prenumerationen är avslutad.");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "";
      showToast(
        msg === "unauthorized"
          ? "Du är utloggad — logga in och försök igen."
          : "Avslutningen gick inte igenom. Prenumerationen löper vidare — försök igen eller kontakta support."
      );
    }
  };

  // Art. 17: raderar på riktigt. Tidigare visade den här bara en toast.
  const confirmDelete = async () => {
    setModal(null);
    showToast("Raderar…");
    try {
      await deleteMyAccount();
      showToast("Kontot och alla uppgifter är raderade.");
      setTimeout(() => {
        window.location.href = "/login";
      }, 1400);
    } catch (e) {
      showToast(
        e instanceof Error && e.message === "unauthorized"
          ? "Du måste vara inloggad för att radera kontot."
          : "Raderingen misslyckades. Ingenting har tagits bort — försök igen."
      );
    }
  };

  // Art. 15/20: laddar ned hela datakopian.
  const onExport = async () => {
    showToast("Skapar din datakopia…");
    try {
      await downloadMyData();
      showToast("Datakopian har laddats ned.");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Exporten misslyckades.");
    }
  };

  return (
    <div
      data-page="account"
      style={{
        fontFamily: '"Lora", Georgia, serif',
        color: "#201f1d",
        maxWidth: 680,
      }}
    >
      <h1
        style={{
          fontFamily: '"Cormorant Garamond", Georgia, serif',
          fontWeight: 400,
          fontSize: 34,
          margin: 0,
          color: NAVY,
        }}
      >
        Konto
      </h1>

      <div style={{ display: "flex", flexDirection: "column", gap: 20, marginTop: 24 }}>
        {/* Plan 29 kr */}
        <div style={card}>
          <div
            style={{
              fontSize: 11.5,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: ACCENT_700,
            }}
          >
            Plan
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              marginTop: 12,
              flexWrap: "wrap",
            }}
          >
            <div style={{ flex: 1, minWidth: 180 }}>
              <span
                style={{
                  fontFamily: '"Cormorant Garamond", Georgia, serif',
                  fontSize: 22,
                  color: NAVY,
                }}
              >
                Skydd
              </span>
              <span
                style={{
                  fontSize: 14,
                  color: "#9b9797",
                  fontFeatureSettings: '"tnum" 1',
                }}
              >
                {" "}
                · 29 kr/mån
                {planActive ? " · förnyas 2026-08-15" : " · avslutad"}
              </span>
            </div>
            <button
              type="button"
              style={ghostBtn()}
              disabled={billingBusy}
              onClick={() => void payWithPaypal("skydd_month")}
            >
              {planActive ? "Byt / förnya PayPal" : "Betala med PayPal"}
            </button>
            <button
              type="button"
              style={ghostBtn()}
              disabled={billingBusy}
              onClick={() => void payWithPaypal("familj_month")}
            >
              Uppgradera Familj 49 kr
            </button>
            {planActive && (
              <button
                type="button"
                onClick={() => setModal("cancel-step1")}
                style={{
                  padding: "9px 16px",
                  border: 0,
                  background: "transparent",
                  fontSize: 13,
                  color: "#9b9797",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                Avsluta prenumeration
              </button>
            )}
          </div>
          <div style={{ fontSize: 12.5, color: "#9b9797", marginTop: 10 }}>
            Betalning via PayPal. Ingen bindningstid — avsluta när du vill (app eller PayPal).
          </div>
        </div>

        {/* Billing */}
        <div style={card}>
          <div
            style={{
              fontSize: 11.5,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: ACCENT_700,
            }}
          >
            Fakturering
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              marginTop: 12,
              fontSize: 14,
              flexWrap: "wrap",
            }}
          >
            <span style={{ flex: 1, minWidth: 160 }}>
              {planActive
                ? "Betalning hanteras via PayPal (prenumeration)."
                : "Ingen aktiv prenumeration — betala via PayPal när du vill aktivera Skydd."}
            </span>
            <button
              type="button"
              style={ghostBtn()}
              onClick={() => {
                window.open("https://www.paypal.com/myaccount/autopay/", "_blank", "noopener,noreferrer");
              }}
            >
              Hantera i PayPal
            </button>
            <a
              href="https://gdpr-nordic.org/support"
              style={{
                border: 0,
                background: "transparent",
                fontSize: 13,
                color: LINK,
                cursor: "pointer",
                fontFamily: "inherit",
                textDecoration: "none",
              }}
            >
              Support & kvitton
            </a>
          </div>
        </div>

        {/* Language + security */}
        <div
          style={{
            ...card,
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 18,
          }}
        >
          <label
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              fontSize: 11.5,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: ACCENT_700,
            }}
          >
            Språk
            <select
              value={lang}
              onChange={(e) => {
                setLang(e.target.value);
                showToast(`Språk: ${LANGUAGES.find((l) => l.code === e.target.value)?.label}`);
              }}
              style={{
                padding: "10px 12px",
                border: "1px solid #d7d3d3",
                borderRadius: 4,
                fontSize: 14,
                background: "#fff",
                color: "#201f1d",
                textTransform: "none",
                letterSpacing: 0,
                fontFamily: "inherit",
              }}
            >
              {LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.label}
                </option>
              ))}
            </select>
          </label>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <span
              style={{
                fontSize: 11.5,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: ACCENT_700,
              }}
            >
              Säkerhet
            </span>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                type="button"
                style={ghostBtn()}
                onClick={() =>
                  showToast("Inloggning sker via engångslänk till din e-post — inget lösenord att byta.")
                }
              >
                Inloggning
              </button>
              <a href="https://gdpr-nordic.org/support" style={{ ...ghostBtn(), display: "inline-block", textDecoration: "none" }}>
                Support
              </a>
            </div>
          </div>
        </div>

        {/* Rights: export + delete */}
        <div style={card}>
          <div
            style={{
              fontSize: 11.5,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: ACCENT_700,
            }}
          >
            Dina rättigheter
          </div>
          <div style={{ display: "flex", gap: 12, marginTop: 14, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={onExport}
              style={{
                padding: "10px 18px",
                border: `1px solid ${NAVY}`,
                borderRadius: 4,
                background: "transparent",
                fontSize: 13.5,
                color: NAVY,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Exportera mina uppgifter
            </button>
            <button
              type="button"
              onClick={() => setModal("delete")}
              style={{
                padding: "10px 18px",
                border: `1px solid ${DANGER}`,
                borderRadius: 4,
                background: "transparent",
                fontSize: 13.5,
                color: DANGER,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Radera konto och data
            </button>
          </div>
        </div>

        <div
          style={{
            border: "1px dashed #d7d3d3",
            borderRadius: 7,
            padding: "18px 24px",
            display: "flex",
            alignItems: "center",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 9,
              background: NAVY,
              color: "#D4AF37",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            EU
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: NAVY }}>Installera appen</div>
            <div style={{ fontSize: 12.5, color: "#9b9797" }}>
              Lägg till på hemskärmen för push-notiser och snabb åtkomst (PWA).
            </div>
          </div>
          <button
            type="button"
            style={ghostBtn()}
            onClick={() => {
              showToast(
                "I Safari: Dela → Lägg till på hemskärmen. I Chrome: Meny → Installera app."
              );
            }}
          >
            Installera
          </button>
        </div>

        <div
          style={{
            fontSize: 12,
            color: "#bab6b6",
            textAlign: "center",
            fontFeatureSettings: '"tnum" 1',
          }}
        >
          app.gdpr-nordic.org · version 1.0.0
        </div>
      </div>

      {/* Modals — no dark patterns: clear cancel always equal prominence */}
      {modal === "cancel-step1" && (
        <ConfirmModal
          title="Avsluta prenumerationen?"
          body="Skyddet gäller månaden ut (till 2026-08-15). Dina bevis och din historik sparas i 90 dagar om du vill komma tillbaka. Ingen skuld, inga frågor."
          confirmLabel="Fortsätt"
          confirmDanger={false}
          onCancel={() => setModal(null)}
          onConfirm={() => setModal("cancel-step2")}
        />
      )}
      {modal === "cancel-step2" && (
        <ConfirmModal
          title="Bekräfta avslut"
          body="Är du säker? Prenumerationen avslutas omedelbart för framtida perioder. Du behåller skydd till 2026-08-15. Du kan alltid starta igen senare."
          confirmLabel="Ja, avsluta"
          confirmDanger
          onCancel={() => setModal(null)}
          onConfirm={confirmCancel}
        />
      )}
      {modal === "delete" && (
        <ConfirmModal
          title="Radera konto och all data?"
          body="Allt raderas permanent och direkt: profil, scanhistorik, ärenden, genererade skrivelser och alla inloggade enheter. Detta kan inte ångras."
          confirmLabel="Radera permanent"
          confirmDanger
          onCancel={() => setModal(null)}
          onConfirm={confirmDelete}
        />
      )}

      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: 28,
            left: "50%",
            transform: "translateX(-50%)",
            background: NAVY,
            color: "#fff",
            borderRadius: 4,
            padding: "12px 22px",
            fontSize: 14,
            zIndex: 80,
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}

function ConfirmModal({
  title,
  body,
  confirmLabel,
  confirmDanger,
  onCancel,
  onConfirm,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  confirmDanger: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(11,31,74,0.28)",
        zIndex: 70,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      onClick={onCancel}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal
        aria-labelledby="confirm-modal-title"
        style={{
          width: 440,
          maxWidth: "100%",
          background: "#fff",
          border: "1px solid color-mix(in srgb, #201f1d 16%, transparent)",
          borderRadius: 7,
          boxShadow: "0 12px 32px color-mix(in srgb, #2d2b2b 22%, transparent)",
          padding: "30px 32px",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="confirm-modal-title"
          style={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontWeight: 600,
            fontSize: 23,
            margin: 0,
            color: NAVY,
          }}
        >
          {title}
        </h2>
        <p style={{ fontSize: 14, lineHeight: 1.65, color: "#7d7979", margin: "12px 0 0" }}>
          {body}
        </p>
        <div
          style={{
            display: "flex",
            gap: 12,
            marginTop: 24,
            justifyContent: "flex-end",
            flexWrap: "wrap",
          }}
        >
          {/* Avbryt first / equal weight — no dark pattern trap */}
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: "10px 18px",
              border: "1px solid color-mix(in srgb, #201f1d 16%, transparent)",
              borderRadius: 4,
              background: "transparent",
              fontSize: 14,
              color: "#605d5d",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Avbryt
          </button>
          <button
            type="button"
            onClick={onConfirm}
            style={{
              padding: "10px 18px",
              border: confirmDanger ? `1px solid ${DANGER}` : `1px solid ${NAVY}`,
              borderRadius: 4,
              background: "transparent",
              fontSize: 14,
              color: confirmDanger ? DANGER : NAVY,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
